// Countries.tsx
import React, { useEffect, useState } from 'react';
import { Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ICountry } from '../../interfaces/Country.interface';
import { ICountryDataTable, IColumnTable } from '../../interfaces/Table.interface';
import { IUser } from '../../interfaces/User.interface';
import Loader from '../../shared/components/Loader/Loader';
import Stats from '../../shared/components/stats/Stats';
import { GetCountryList } from '../../services/Country.service';
import { useSnackbar } from '../../services/SnackbarUtils';
import { isManagerOrAdmin } from '../../utils/role';
import './Countries.scss';

interface ICountriesProps {
  currentUser: IUser;
}

const Countries = (props: ICountriesProps): JSX.Element => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const isAllowed = isManagerOrAdmin(props.currentUser);

  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>();
  const [countriesSearchValue, setCountriesSearchValue] = useState('');
  const [countries, setCountries] = useState<ICountry[]>([]);

  const { snackBarOpen, snackbarMessage, snackBarType, showSnackbar, handleSnackBarClose } =
    useSnackbar();

  const columnsTable: IColumnTable[] = [
    { accessorKey: 'name', header: t('country') },
    { accessorKey: 'emailRecipientsCount', header: t('email_recipients') },
  ];

  const fetchCountries = (): void => {
    setIsRequestInProgress(true);
    GetCountryList()
      .then((countriesList) => {
        setIsRequestInProgress(false);
        setCountries(countriesList);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  // The list is short and fully loaded, so searching stays client side.
  const getCountriesDataTable = (): ICountryDataTable[] => {
    const search = countriesSearchValue.trim().toLowerCase();

    return countries
      .filter((country) => search.length === 0 || country.name_normalized.includes(search))
      .map((country) => ({
        id: country.id,
        name: country.name,
        emailRecipientsCount: country.email_recipients_count,
      }));
  };

  const handleSubmitSearch = (value: string): void => {
    setCountriesSearchValue(value);
  };

  const searchHandleClear = (): void => {
    setCountriesSearchValue('');
  };

  const countriesRowClicked = (countryId: string): void => {
    navigate(`/countries/details/${countryId}`);
  };

  useEffect(() => {
    if (isAllowed) {
      fetchCountries();
    }
  }, [isAllowed]);

  if (!isAllowed) {
    return (
      <div className="countries">
        <p className="not-authorized">{t('not_authorized_to_view_this_section')}</p>
      </div>
    );
  }

  return (
    <div className="countries">
      {isRequestInProgress === true && <Loader />}

      <Stats
        title={t('country_management')}
        searchValue={countriesSearchValue}
        onSearchSubmit={handleSubmitSearch}
        onSearchClear={searchHandleClear}
        dataTable={getCountriesDataTable()}
        columnsTable={columnsTable}
        emptyMsgTable={isRequestInProgress === false ? t('no_countries_to_display') : ''}
        onTableRowClicked={countriesRowClicked}
        tableTotalPages={0}
        searchPlaceholder={t('country_search_placeholder')}
      />

      <Snackbar
        className={`oryx-snackbar ${snackBarType}`}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackBarOpen}
        message={snackbarMessage}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}
      />
    </div>
  );
};

export default Countries;
